import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_CONFIG, buildApiUrl } from '../config';
import { getTutorialManager } from '../utils/tutorialManager';
import { 
  syncOperationsTypes, 
  syncLocations, 
  syncRadioChannels, 
  syncTasks,
  addLocationWithCacheClear,
  addRadioChannelWithCacheClear,
  addTaskWithCacheClear,
  addOperationsTypeWithCacheClear
} from '../utils/syncThrottled';

export interface OperationType {
  id?: number;
  name: string;
  isSelected: boolean;
}

export interface Location {
  id?: number;
  name: string;
  isSelected: boolean;
}

export interface RadioChannel {
  id?: number;
  name: string;
  isSelected: boolean;
}

export interface Task {
  id?: number;
  name: string;
  isSelected: boolean;
}

export interface TutorialData {
  operationsTypes: OperationType[];
  locations: Location[];
  radioChannels: RadioChannel[];
  tasks: Task[];
}

export interface TutorialState {
  isActive: boolean;
  currentStep: number;
  isCompleted: boolean;
  data: TutorialData;
}

interface TutorialContextType {
  state: TutorialState;
  startTutorial: () => void;
  completeStep: (step: number) => void;
  updateData: (category: keyof TutorialData, items: any[]) => void;
  saveTutorialData: () => Promise<void>;
  skipTutorial: () => void;
  resetTutorial: () => void;
  resetTutorialIfNeeded: () => void;
  forceResetTutorial: () => void;
}

const defaultTutorialData: TutorialData = {
  operationsTypes: [
    { name: 'Brand', isSelected: true }
  ],
  locations: [
    { name: 'EG', isSelected: true },
    { name: '1. OG', isSelected: true }
  ],
  radioChannels: [
    { name: 'Kanal 1', isSelected: true },
    { name: 'Kanal 2', isSelected: true }
  ],
  tasks: [
    { name: 'Löschen', isSelected: true },
    { name: 'Sichern', isSelected: true },
    { name: 'Absuchen', isSelected: true },
    { name: 'Retten', isSelected: true }
  ]
};

const defaultTutorialState: TutorialState = {
  isActive: false,
  currentStep: 1,
  isCompleted: false,
  data: defaultTutorialData
};

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: React.ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  // Tutorial-State aus localStorage laden oder Default verwenden
  const [state, setState] = useState<TutorialState>(() => {
    try {
      const savedState = localStorage.getItem('tutorialState');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        console.log('📱 Tutorial-State aus localStorage geladen:', parsed);
        
        // Validiere den geladenen State
        if (parsed.currentStep > 6 || parsed.currentStep < 1) {
          console.warn('⚠️ Ungültiger Tutorial-Step gefunden:', parsed.currentStep, '- setze zurück auf Schritt 1');
          const validState = {
            ...parsed,
            currentStep: 1,
            isActive: false,
            isCompleted: false
          };
          localStorage.setItem('tutorialState', JSON.stringify(validState));
          return validState;
        }
        
        return parsed;
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden des Tutorial-States:', error);
    }
    
    // Fallback: Verwende Default-State
    console.log('📱 Verwende Default-Tutorial-State');
    return defaultTutorialState;
  });

  const startTutorial = async () => {
    console.log('🚀 startTutorial() wurde aufgerufen - setze Tutorial aktiv');
    
    // Zuerst den State zurücksetzen, falls er korrupt ist
    resetTutorialIfNeeded();
    
    // Tutorial sofort als aktiv setzen
    setState(prev => {
      const newState = {
        ...prev,
        isActive: true,
        currentStep: 1,
        isCompleted: false
      };
      localStorage.setItem('tutorialState', JSON.stringify(newState));
      return newState;
    });

    try {
      // Lade echte Daten von der API über syncThrottled
      const { 
        syncAllOperationsTypes, 
        syncLocations, 
        syncRadioChannels, 
        syncTasks 
      } = await import('../utils/syncThrottled');
      
      // Lade alle verfügbaren Daten (der Interceptor kümmert sich um Auth)
      const [operationsTypes, locations, radioChannels, tasks] = await Promise.all([
        syncAllOperationsTypes(),
        syncLocations(),
        syncRadioChannels(),
        syncTasks()
      ]);
      
      // Konvertiere die API-Daten in das Tutorial-Format
      const apiData: TutorialData = {
        operationsTypes: (operationsTypes || []).map((item: any) => ({ 
          id: item.id, // WICHTIG: ID beibehalten!
          name: item.name, 
          isSelected: false 
        })),
        locations: defaultTutorialData.locations, // Behalte Default-Daten mit isSelected: true
        radioChannels: defaultTutorialData.radioChannels, // Behalte Default-Daten mit isSelected: true
        tasks: defaultTutorialData.tasks // Behalte Default-Daten mit isSelected: true
      };
      
      console.log('📡 API-Daten geladen:', apiData);
      
      // Aktualisiere den State mit den API-Daten
      setState(prev => {
        const newState = {
          ...prev,
          data: apiData
        };
        localStorage.setItem('tutorialState', JSON.stringify(newState));
        console.log('✅ Tutorial-State wurde mit API-Daten aktualisiert');
        return newState;
      });
    } catch (error) {
      console.error('❌ Fehler beim Laden der API-Daten - Tutorial läuft mit Default-Daten weiter:', error);
      // Tutorial läuft weiter mit Default-Daten
    }
  };

  const completeStep = (step: number) => {
    console.log('🔄 completeStep aufgerufen mit step:', step);
    console.log('🔄 Aktueller Schritt vor Änderung:', state.currentStep);
    setState(prev => {
      const newStep = step + 1;
      console.log('🔄 Neuer Schritt wird gesetzt auf:', newStep);
      const newState = {
        ...prev,
        currentStep: newStep
      };
      // State in localStorage speichern
      localStorage.setItem('tutorialState', JSON.stringify(newState));
      console.log('✅ Tutorial-State wurde aktualisiert und gespeichert');
      return newState;
    });
  };

  const updateData = (category: keyof TutorialData, items: any[]) => {
    setState(prev => {
      const newState = {
        ...prev,
        data: {
          ...prev.data,
          [category]: items
        }
      };
      // State in localStorage speichern
      localStorage.setItem('tutorialState', JSON.stringify(newState));
      return newState;
    });
  };

  const saveTutorialData = async () => {
    try {
      // Hier werden die Daten an die API gesendet
      console.log('💾 Tutorial-Daten werden gespeichert:', state.data);
      
      // API-Calls für das Speichern der Daten
      // Hole die aktuelle Organisation aus dem selectedOrgID (primär) oder Access-Token (fallback)
      let orgID: number | null = null;
      
      // 1. Versuche selectedOrgID aus localStorage zu holen (aktuelle Benutzerauswahl)
      const selectedOrgID = localStorage.getItem('selectedOrgID');
      if (selectedOrgID) {
        const parsedSelectedOrgID = parseInt(selectedOrgID);
        if (!isNaN(parsedSelectedOrgID)) {
          orgID = parsedSelectedOrgID;
          console.log('💾 Speichere Tutorial-Daten für selectedOrgID:', orgID);
        }
      }
      
      // 2. Fallback: Hole orgID aus dem Access-Token
      if (!orgID) {
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            orgID = payload.orgID;
            console.log('💾 Speichere Tutorial-Daten für orgID aus JWT:', orgID);
          } catch (error) {
            console.error('❌ Fehler beim Parsen des Access-Tokens:', error);
          }
        }
      }

      if (!orgID) {
        throw new Error('Keine Organisation gefunden - kann Tutorial-Daten nicht speichern');
      }
      
      console.log('🏢 Speichere Daten für Organisation:', orgID);
      
      // 1. Locations speichern
      for (const location of state.data.locations) {
        if (location.isSelected) {
          console.log('📍 Speichere Location:', location.name);
          const success = await addLocationWithCacheClear(location.name);
          if (!success) {
            throw new Error(`Fehler beim Speichern der Location ${location.name}`);
          }
          console.log('✅ Location gespeichert:', location.name);
        }
      }
      
      // 2. Radio Channels speichern
      for (const channel of state.data.radioChannels) {
        if (channel.isSelected) {
          console.log('📻 Speichere Radio Channel:', channel.name);
          const success = await addRadioChannelWithCacheClear(channel.name);
          if (!success) {
            throw new Error(`Fehler beim Speichern des Radio Channels ${channel.name}`);
          }
          console.log('✅ Radio Channel gespeichert:', channel.name);
        }
      }
      
      // 3. Tasks speichern
      for (const task of state.data.tasks) {
        if (task.isSelected) {
          console.log('📋 Speichere Task:', task.name);
          const success = await addTaskWithCacheClear(task.name);
          if (!success) {
            throw new Error(`Fehler beim Speichern der Task ${task.name}`);
          }
          console.log('✅ Task gespeichert:', task.name);
        }
      }
      
      // 4. Operations Types verknüpfen
      for (const opType of state.data.operationsTypes) {
        if (opType.isSelected) {
          console.log('🚒 Verknüpfe Operations Type:', opType.name, 'mit ID:', opType.id);
          
          if (opType.id) {
            // Operations Type existiert bereits, verknüpfe ihn mit der Organisation
            console.log('🔗 Verknüpfe existierenden Operations Type ID:', opType.id);
            const success = await addOperationsTypeWithCacheClear(opType.id);
            console.log('🔗 Verknüpfung erfolgreich:', success);
            if (!success) {
              throw new Error(`Fehler beim Verknüpfen des Operations Types ${opType.name} (ID: ${opType.id})`);
            }
            console.log('✅ Operations Type verknüpft:', opType.name);
          } else {
            // Operations Type existiert nicht, erstelle ihn zuerst
            console.log('🚒 Erstelle neuen Operations Type:', opType.name);
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_TYPES_ADD), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
              },
              body: JSON.stringify({
                name: opType.name
              })
            });
            
            if (!response.ok) {
              throw new Error(`Fehler beim Erstellen des Operations Types ${opType.name}: ${response.status}`);
            }
            
            const newOpType = await response.json();
            console.log('✅ Neuer Operations Type erstellt:', opType.name, 'mit ID:', newOpType.id);
            
            // Jetzt verknüpfe ihn mit der Organisation
            const linkSuccess = await addOperationsTypeWithCacheClear(newOpType.id);
            if (!linkSuccess) {
              throw new Error(`Fehler beim Verknüpfen des neuen Operations Types ${opType.name}`);
            }
            console.log('✅ Neuer Operations Type verknüpft:', opType.name);
          }
        } else {
          console.log('⏭️ Operations Type übersprungen (nicht ausgewählt):', opType.name);
        }
      }
      
      console.log('🎉 Alle Tutorial-Daten erfolgreich gespeichert!');
      
      setState(prev => ({
        ...prev,
        isCompleted: true
      }));
      
      // Tutorial als abgeschlossen markieren
      localStorage.setItem('tutorialCompleted', 'true');
      
      return Promise.resolve();
    } catch (error) {
      console.error('❌ Fehler beim Speichern der Tutorial-Daten:', error);
      return Promise.reject(error);
    }
  };

  const skipTutorial = () => {
    setState(prev => {
      const newState = {
        ...prev,
        isActive: false,
        isCompleted: true
      };
      // State in localStorage speichern
      localStorage.setItem('tutorialState', JSON.stringify(newState));
      // Tutorial als abgeschlossen markieren
      localStorage.setItem('tutorialCompleted', 'true');
      return newState;
    });
    // Tutorial beenden und zum Dashboard weiterleiten
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 100);
  };

  const resetTutorial = () => {
    setState(defaultTutorialState);
    // State aus localStorage löschen
    localStorage.removeItem('tutorialState');
  };

  const resetTutorialIfNeeded = () => {
    // Überprüfe, ob der Tutorial-State ungültig ist (z.B. Schritt > 6)
    if (state.currentStep > 6 || state.currentStep < 1) {
      console.warn('⚠️ Ungültiger Tutorial-Step gefunden, setze zurück auf Schritt 1');
      resetTutorial(); // Verwende die vorhandene resetTutorial-Funktion
    }
  };

  const forceResetTutorial = () => {
    console.log('🔄 Erzwinge Tutorial-Reset für neuen Start');
    resetTutorial();
  };

  const value: TutorialContextType = {
    state,
    startTutorial,
    completeStep,
    updateData,
    saveTutorialData,
    skipTutorial,
    resetTutorial,
    resetTutorialIfNeeded,
    forceResetTutorial
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
